import type { StateCreator } from 'zustand';
import type {
  SwotAnalysis,
  SwotItem,
  SwotQuadrant,
  SwotConfig,
  SwotFilter,
  SwotStatistics,
  SwotPriority,
  SwotActionStatus,
  SwotStrategy,
} from '../../types/swot';
import { DEFAULT_SWOT_CONFIG } from '../../types/swot';

// ============================================================================
// SWOT ANALYSIS SLICE
// ============================================================================

export interface SwotSlice {
  // State
  swotAnalysis: SwotAnalysis | null;
  swotConfig: SwotConfig;
  swotFilter: SwotFilter;
  selectedItemId: string | null;

  // Actions - CRUD operations
  addSwotItem: (quadrant: SwotQuadrant, item: Partial<SwotItem>) => void;
  updateSwotItem: (id: string, updates: Partial<SwotItem>) => void;
  deleteSwotItem: (id: string) => void;
  duplicateSwotItem: (id: string) => void;

  // Actions - Item management
  moveSwotItem: (id: string, targetQuadrant: SwotQuadrant) => void;
  updateItemPriority: (id: string, priority: SwotPriority) => void;
  updateItemStatus: (id: string, status: SwotActionStatus) => void;
  assignItem: (id: string, assigneeId: string) => void;
  addItemNote: (id: string, content: string, author?: string) => void;
  completeItem: (id: string) => void;

  // Actions - Strategies
  addStrategy: (strategy: Partial<SwotStrategy>) => void;
  updateStrategy: (id: string, updates: Partial<SwotStrategy>) => void;
  deleteStrategy: (id: string) => void;

  // Actions - Configuration
  updateSwotConfig: (config: Partial<SwotConfig>) => void;
  resetSwotConfig: () => void;

  // Actions - Filtering & Selection
  setSwotFilter: (filter: Partial<SwotFilter>) => void;
  clearSwotFilter: () => void;
  selectItem: (id: string | null) => void;

  // Computed
  getFilteredItems: () => SwotItem[];
  getItemById: (id: string) => SwotItem | undefined;
  getItemsByQuadrant: (quadrant: SwotQuadrant) => SwotItem[];
  getSwotStatistics: () => SwotStatistics;
  initializeSwot: () => void;
}

export const createSwotSlice: StateCreator<any, [], [], SwotSlice> = (set, get) => {
  const canEdit = (): boolean => {
    const state = get();
    const role = state.currentProject?.currentUserRole;
    if (role) {
      const allowed = role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR';
      if (!allowed) {
        state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      }
      return allowed;
    }
    if (state.storageMode === 'backend') {
      state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      return false;
    }
    return true;
  };

  return ({
  // Initial state
  swotAnalysis: null,
  swotConfig: DEFAULT_SWOT_CONFIG,
  swotFilter: {},
  selectedItemId: null,

  // Initialize SWOT analysis for current project
  initializeSwot: () => {
    if (!canEdit()) return;
    const { currentProject, swotAnalysis } = get();

    if (!currentProject) {
      get().addNotification('error', 'Aucun projet actif');
      return;
    }

    // Only initialize if no SWOT analysis exists
    if (!swotAnalysis || swotAnalysis.projectId !== currentProject.id) {
      const newSwotAnalysis: SwotAnalysis = {
        id: `swot-${currentProject.id}`,
        projectId: currentProject.id,
        strengths: [],
        weaknesses: [],
        opportunities: [],
        threats: [],
        strategies: [],
        createdAt: new Date(),
        lastUpdated: new Date(),
        version: 1,
      };

      set({ swotAnalysis: newSwotAnalysis });
      get().addNotification('success', 'Analyse SWOT initialisée');
    }
  },

  // Add new SWOT item
  addSwotItem: (quadrant, itemData) => {
    if (!canEdit()) return;
    const { swotAnalysis, currentProject } = get();

    if (!currentProject) {
      get().addNotification('error', 'Aucun projet actif');
      return;
    }

    if (!swotAnalysis) {
      get().initializeSwot();
    }

    const analysis = get().swotAnalysis;
    if (!analysis) return;

    const newItem: SwotItem = {
      id: `swot-item-${Date.now()}`,
      quadrant,
      title: itemData.title || 'Nouvel élément',
      description: itemData.description || '',
      priority: itemData.priority || 'medium',
      impact: itemData.impact || 3,
      actionPlan: itemData.actionPlan,
      actionStatus: itemData.actionStatus || 'pending',
      assignedTo: itemData.assignedTo,
      createdAt: new Date(),
      updatedAt: new Date(),
      tags: itemData.tags || [],
      relatedItems: itemData.relatedItems || [],
      notes: itemData.notes || [],
    };

    const updatedAnalysis = {
      ...analysis,
      [quadrant]: [...analysis[quadrant], newItem],
      lastUpdated: new Date(),
      version: analysis.version + 1,
    };

    set({ swotAnalysis: updatedAnalysis });
    get().addNotification('success', `Élément ajouté: ${newItem.title}`);
  },

  // Update existing SWOT item
  updateSwotItem: (id, updates) => {
    if (!canEdit()) return;
    const { swotAnalysis } = get();
    if (!swotAnalysis) return;

    let itemFound = false;
    let updatedAnalysis = { ...swotAnalysis };

    // Find and update the item in the correct quadrant
    (['strengths', 'weaknesses', 'opportunities', 'threats'] as SwotQuadrant[]).forEach(
      (quadrant) => {
        const itemIndex = swotAnalysis[quadrant].findIndex((item: SwotItem) => item.id === id);
        if (itemIndex !== -1) {
          const updatedItems = [...swotAnalysis[quadrant]];
          updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            ...updates,
            updatedAt: new Date(),
          };
          updatedAnalysis[quadrant] = updatedItems;
          itemFound = true;
        }
      }
    );

    if (itemFound) {
      updatedAnalysis.lastUpdated = new Date();
      updatedAnalysis.version += 1;
      set({ swotAnalysis: updatedAnalysis });
      get().addNotification('success', 'Élément mis à jour');
    } else {
      get().addNotification('error', 'Élément introuvable');
    }
  },

  // Delete SWOT item
  deleteSwotItem: (id) => {
    if (!canEdit()) return;
    const { swotAnalysis } = get();
    if (!swotAnalysis) return;

    let itemFound = false;
    let updatedAnalysis = { ...swotAnalysis };

    (['strengths', 'weaknesses', 'opportunities', 'threats'] as SwotQuadrant[]).forEach(
      (quadrant) => {
        const filteredItems = swotAnalysis[quadrant].filter((item: SwotItem) => item.id !== id);
        if (filteredItems.length !== swotAnalysis[quadrant].length) {
          updatedAnalysis[quadrant] = filteredItems;
          itemFound = true;
        }
      }
    );

    if (itemFound) {
      updatedAnalysis.lastUpdated = new Date();
      updatedAnalysis.version += 1;
      set({ swotAnalysis: updatedAnalysis });
      get().addNotification('success', 'Élément supprimé');
    }
  },

  // Duplicate SWOT item
  duplicateSwotItem: (id) => {
    if (!canEdit()) return;
    const item = get().getItemById(id);
    if (!item) {
      get().addNotification('error', 'Élément introuvable');
      return;
    }

    get().addSwotItem(item.quadrant, {
      ...item,
      id: undefined,
      title: `${item.title} (Copie)`,
      createdAt: new Date(),
      updatedAt: new Date(),
      completedAt: undefined,
      actionStatus: 'pending',
    });
  },

  // Move item to another quadrant
  moveSwotItem: (id, targetQuadrant) => {
    if (!canEdit()) return;
    const { swotAnalysis } = get();
    if (!swotAnalysis) return;

    let item: SwotItem | undefined;
    let sourceQuadrant: SwotQuadrant | undefined;

    // Find the item
    (['strengths', 'weaknesses', 'opportunities', 'threats'] as SwotQuadrant[]).forEach(
      (quadrant) => {
        const foundItem = swotAnalysis[quadrant].find((i: SwotItem) => i.id === id);
        if (foundItem) {
          item = foundItem;
          sourceQuadrant = quadrant;
        }
      }
    );

    if (!item || !sourceQuadrant) {
      get().addNotification('error', 'Élément introuvable');
      return;
    }

    if (sourceQuadrant === targetQuadrant) {
      return; // No change needed
    }

    const updatedAnalysis = { ...swotAnalysis };

    // Remove from source quadrant
    updatedAnalysis[sourceQuadrant] = swotAnalysis[sourceQuadrant].filter((i: SwotItem) => i.id !== id);

    // Add to target quadrant
    updatedAnalysis[targetQuadrant] = [
      ...swotAnalysis[targetQuadrant],
      { ...item, quadrant: targetQuadrant, updatedAt: new Date() },
    ];

    updatedAnalysis.lastUpdated = new Date();
    updatedAnalysis.version += 1;

    set({ swotAnalysis: updatedAnalysis });
    get().addNotification('success', 'Élément déplacé');
  },

  // Update item priority
  updateItemPriority: (id, priority) => {
    if (!canEdit()) return;
    get().updateSwotItem(id, { priority });
  },

  // Update item status
  updateItemStatus: (id, status) => {
    if (!canEdit()) return;
    get().updateSwotItem(id, { actionStatus: status });
  },

  // Assign item
  assignItem: (id, assigneeId) => {
    if (!canEdit()) return;
    get().updateSwotItem(id, { assignedTo: assigneeId });
    get().addNotification('success', 'Responsable assigné');
  },

  // Add note to item
  addItemNote: (id, content, author) => {
    if (!canEdit()) return;
    const item = get().getItemById(id);
    if (!item) {
      get().addNotification('error', 'Élément introuvable');
      return;
    }

    const note = {
      id: `note-${Date.now()}`,
      itemId: id,
      author,
      content,
      createdAt: new Date(),
    };

    get().updateSwotItem(id, {
      notes: [...(item.notes || []), note],
    });

    get().addNotification('success', 'Note ajoutée');
  },

  // Complete item
  completeItem: (id) => {
    if (!canEdit()) return;
    get().updateSwotItem(id, {
      actionStatus: 'completed',
      completedAt: new Date(),
    });
    get().addNotification('success', 'Élément marqué comme terminé');
  },

  // Add strategy
  addStrategy: (strategyData) => {
    if (!canEdit()) return;
    const { swotAnalysis } = get();
    if (!swotAnalysis) {
      get().initializeSwot();
    }

    const analysis = get().swotAnalysis;
    if (!analysis) return;

    const newStrategy: SwotStrategy = {
      id: `strategy-${Date.now()}`,
      type: strategyData.type || 'SO',
      title: strategyData.title || 'Nouvelle stratégie',
      description: strategyData.description || '',
      relatedStrengths: strategyData.relatedStrengths || [],
      relatedWeaknesses: strategyData.relatedWeaknesses || [],
      relatedOpportunities: strategyData.relatedOpportunities || [],
      relatedThreats: strategyData.relatedThreats || [],
      priority: strategyData.priority || 'medium',
      status: strategyData.status || 'pending',
      createdAt: new Date(),
    };

    const updatedAnalysis = {
      ...analysis,
      strategies: [...(analysis.strategies || []), newStrategy],
      lastUpdated: new Date(),
      version: analysis.version + 1,
    };

    set({ swotAnalysis: updatedAnalysis });
    get().addNotification('success', 'Stratégie ajoutée');
  },

  // Update strategy
  updateStrategy: (id, updates) => {
    if (!canEdit()) return;
    const { swotAnalysis } = get();
    if (!swotAnalysis || !swotAnalysis.strategies) return;

    const strategyIndex = swotAnalysis.strategies.findIndex((s: SwotStrategy) => s.id === id);
    if (strategyIndex === -1) {
      get().addNotification('error', 'Stratégie introuvable');
      return;
    }

    const updatedStrategies = [...swotAnalysis.strategies];
    updatedStrategies[strategyIndex] = {
      ...updatedStrategies[strategyIndex],
      ...updates,
    };

    const updatedAnalysis = {
      ...swotAnalysis,
      strategies: updatedStrategies,
      lastUpdated: new Date(),
      version: swotAnalysis.version + 1,
    };

    set({ swotAnalysis: updatedAnalysis });
    get().addNotification('success', 'Stratégie mise à jour');
  },

  // Delete strategy
  deleteStrategy: (id) => {
    if (!canEdit()) return;
    const { swotAnalysis } = get();
    if (!swotAnalysis || !swotAnalysis.strategies) return;

    const updatedAnalysis = {
      ...swotAnalysis,
      strategies: swotAnalysis.strategies.filter((s: SwotStrategy) => s.id !== id),
      lastUpdated: new Date(),
      version: swotAnalysis.version + 1,
    };

    set({ swotAnalysis: updatedAnalysis });
    get().addNotification('success', 'Stratégie supprimée');
  },

  // Update configuration
  updateSwotConfig: (config) => {
    if (!canEdit()) return;
    set((state: any) => ({
      swotConfig: {
        ...state.swotConfig,
        ...config,
      },
    }));
  },

  // Reset configuration
  resetSwotConfig: () => {
    if (!canEdit()) return;
    set({ swotConfig: DEFAULT_SWOT_CONFIG });
    get().addNotification('info', 'Configuration réinitialisée');
  },

  // Set filter
  setSwotFilter: (filter) => {
    set((state: any) => ({
      swotFilter: {
        ...state.swotFilter,
        ...filter,
      },
    }));
  },

  // Clear filter
  clearSwotFilter: () => {
    set({ swotFilter: {} });
  },

  // Select item
  selectItem: (id) => {
    set({ selectedItemId: id });
  },

  // Get filtered items
  getFilteredItems: () => {
    const { swotAnalysis, swotFilter, swotConfig } = get();
    if (!swotAnalysis) return [];

    let allItems: SwotItem[] = [
      ...swotAnalysis.strengths,
      ...swotAnalysis.weaknesses,
      ...swotAnalysis.opportunities,
      ...swotAnalysis.threats,
    ];

    // Apply config filters
    if (!swotConfig.showCompletedItems) {
      allItems = allItems.filter((item) => item.actionStatus !== 'completed');
    }

    // Apply quadrant filter
    if (swotFilter.quadrants && swotFilter.quadrants.length > 0) {
      allItems = allItems.filter((item) => swotFilter.quadrants!.includes(item.quadrant));
    }

    // Apply priority filter
    if (swotFilter.priorities && swotFilter.priorities.length > 0) {
      allItems = allItems.filter((item) => swotFilter.priorities!.includes(item.priority));
    }

    // Apply status filter
    if (swotFilter.statuses && swotFilter.statuses.length > 0) {
      allItems = allItems.filter((item) =>
        item.actionStatus ? swotFilter.statuses!.includes(item.actionStatus) : false
      );
    }

    // Apply assignee filter
    if (swotFilter.assignees && swotFilter.assignees.length > 0) {
      allItems = allItems.filter((item) =>
        item.assignedTo ? swotFilter.assignees!.includes(item.assignedTo) : false
      );
    }

    // Apply tag filter
    if (swotFilter.tags && swotFilter.tags.length > 0) {
      allItems = allItems.filter((item) =>
        swotFilter.tags!.some((tag: string) => item.tags.includes(tag))
      );
    }

    // Apply search query
    if (swotFilter.searchQuery && swotFilter.searchQuery.trim().length > 0) {
      const query = swotFilter.searchQuery.toLowerCase();
      allItems = allItems.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query) ||
          item.tags.some((tag: string) => tag.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    switch (swotConfig.sortBy) {
      case 'priority':
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        allItems.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
        break;
      case 'date':
        allItems.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        break;
      case 'impact':
        allItems.sort((a, b) => b.impact - a.impact);
        break;
      case 'status':
        allItems.sort((a, b) =>
          (a.actionStatus || '').localeCompare(b.actionStatus || '')
        );
        break;
    }

    return allItems;
  },

  // Get item by ID
  getItemById: (id) => {
    const { swotAnalysis } = get();
    if (!swotAnalysis) return undefined;

    const allItems = [
      ...swotAnalysis.strengths,
      ...swotAnalysis.weaknesses,
      ...swotAnalysis.opportunities,
      ...swotAnalysis.threats,
    ];

    return allItems.find((item) => item.id === id);
  },

  // Get items by quadrant
  getItemsByQuadrant: (quadrant) => {
    const { swotAnalysis, swotConfig } = get();
    if (!swotAnalysis) return [];

    let items = [...swotAnalysis[quadrant]];

    // Apply config filters
    if (!swotConfig.showCompletedItems) {
      items = items.filter((item) => item.actionStatus !== 'completed');
    }

    return items;
  },

  // Get SWOT statistics
  getSwotStatistics: () => {
    const { swotAnalysis, swotConfig } = get();

    if (!swotAnalysis) {
      return {
        totalItems: 0,
        byQuadrant: { strengths: 0, weaknesses: 0, opportunities: 0, threats: 0 },
        byPriority: { low: 0, medium: 0, high: 0, critical: 0 },
        byStatus: { pending: 0, in_progress: 0, completed: 0, cancelled: 0 },
        strengthScore: 0,
        weaknessScore: 0,
        opportunityScore: 0,
        threatScore: 0,
        overallBalance: 0,
        strategicPosition: 'conservative' as const,
        topStrengths: [],
        topWeaknesses: [],
        topOpportunities: [],
        topThreats: [],
        completionRate: 0,
      };
    }

    const allItems = [
      ...swotAnalysis.strengths,
      ...swotAnalysis.weaknesses,
      ...swotAnalysis.opportunities,
      ...swotAnalysis.threats,
    ];

    const activeItems = swotConfig.showCompletedItems
      ? allItems
      : allItems.filter((item) => item.actionStatus !== 'completed');

    // Count by quadrant
    const byQuadrant = {
      strengths: swotAnalysis.strengths.length,
      weaknesses: swotAnalysis.weaknesses.length,
      opportunities: swotAnalysis.opportunities.length,
      threats: swotAnalysis.threats.length,
    };

    // Count by priority
    const byPriority: Record<SwotPriority, number> = { low: 0, medium: 0, high: 0, critical: 0 };
    activeItems.forEach((item: SwotItem) => {
      byPriority[item.priority]++;
    });

    // Count by status
    const byStatus: Record<SwotActionStatus, number> = { pending: 0, in_progress: 0, completed: 0, cancelled: 0 };
    allItems.forEach((item: SwotItem) => {
      if (item.actionStatus) {
        byStatus[item.actionStatus]++;
      }
    });

    // Calculate scores
    const strengthScore = swotAnalysis.strengths.reduce((sum: number, item: SwotItem) => sum + item.impact, 0);
    const weaknessScore = swotAnalysis.weaknesses.reduce((sum: number, item: SwotItem) => sum + item.impact, 0);
    const opportunityScore = swotAnalysis.opportunities.reduce(
      (sum: number, item: SwotItem) => sum + item.impact,
      0
    );
    const threatScore = swotAnalysis.threats.reduce((sum: number, item: SwotItem) => sum + item.impact, 0);

    // Calculate overall balance
    const overallBalance = strengthScore + opportunityScore - weaknessScore - threatScore;

    // Determine strategic position
    let strategicPosition: 'aggressive' | 'conservative' | 'competitive' | 'defensive';
    if (strengthScore > weaknessScore && opportunityScore > threatScore) {
      strategicPosition = 'aggressive'; // SO strategies
    } else if (strengthScore < weaknessScore && opportunityScore > threatScore) {
      strategicPosition = 'conservative'; // WO strategies
    } else if (strengthScore > weaknessScore && opportunityScore < threatScore) {
      strategicPosition = 'competitive'; // ST strategies
    } else {
      strategicPosition = 'defensive'; // WT strategies
    }

    // Get top items (sorted by impact and priority)
    const sortByImpact = (items: SwotItem[]) =>
      [...items].sort((a, b) => {
        if (b.impact !== a.impact) return b.impact - a.impact;
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });

    const topStrengths = sortByImpact(swotAnalysis.strengths).slice(0, 3);
    const topWeaknesses = sortByImpact(swotAnalysis.weaknesses).slice(0, 3);
    const topOpportunities = sortByImpact(swotAnalysis.opportunities).slice(0, 3);
    const topThreats = sortByImpact(swotAnalysis.threats).slice(0, 3);

    // Calculate completion rate
    const itemsWithActions = allItems.filter((item) => item.actionStatus);
    const completedItems = allItems.filter((item) => item.actionStatus === 'completed');
    const completionRate =
      itemsWithActions.length > 0 ? (completedItems.length / itemsWithActions.length) * 100 : 0;

    return {
      totalItems: activeItems.length,
      byQuadrant,
      byPriority,
      byStatus,
      strengthScore,
      weaknessScore,
      opportunityScore,
      threatScore,
      overallBalance,
      strategicPosition,
      topStrengths,
      topWeaknesses,
      topOpportunities,
      topThreats,
      completionRate,
    };
  },
  });
};
